import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  FaCheckCircle,
  FaShieldAlt,
  FaAward,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaHeartbeat,
  FaGraduationCap,
  FaChevronRight
} from "react-icons/fa";
import { motion, AnimatePresence } from "motion/react";
import "../Styles/CanadaPR.css";
import Toast from "./Toast";
import Reveal, { RevealGroup, RevealItem } from "./Reveal";
import MagneticButton from "./MagneticButton";

const pnpPrograms = [
  {
    name: "Ontario (OINP)",
    desc: "Direct notifications for Tech and Healthcare professionals via Express Entry streams.",
    wages: "$78,000 - $115,000/yr",
    demands: ["Software Developers", "Registered Nurses", "Financial Analysts", "Data Engineers"],
    stream: "Human Capital Priorities Pathway",
    details: "Ontario regularly invites candidates directly from the Express Entry pool. Tech and healthcare draws target specific profiles, often with lower score thresholds."
  },
  {
    name: "British Columbia (BC PNP)",
    desc: "Point-based invitation system targeting tech workers and local graduates.",
    wages: "$82,000 - $125,000/yr",
    demands: ["Cloud Architects", "Civil Engineers", "Early Childhood Educators", "Clinical Pharmacists"],
    stream: "BC Express Entry Tech Stream",
    details: "BC PNP Tech offers priority processing and weekly invitation draws for qualified tech professionals holding a valid 1-year job offer in one of 35 key occupations."
  },
  {
    name: "Alberta (AAIP)",
    desc: "Dedicated streams for accelerated pathways and family-connection ties.",
    wages: "$72,000 - $110,000/yr",
    demands: ["Mechanical Technicians", "Logistics Managers", "Construction Leads", "IT Specialists"],
    stream: "Accelerated Tech Pathway",
    details: "Alberta offers fast-track nomination for Express Entry candidates who are currently working or have a job offer in Alberta's expanding tech sector."
  },
  {
    name: "Saskatchewan (SINP)",
    desc: "Popular for skilled workers with profiles in the Saskatchewan EOI pool.",
    wages: "$68,000 - $98,000/yr",
    demands: ["Agricultural Managers", "Heavy-Duty Mechanics", "Project Estimators", "Medical Lab Technicians"],
    stream: "Occupations In-Demand Stream",
    details: "Saskatchewan targets skilled professionals with experience in highly in-demand local occupations. A formal Canadian job offer is not mandatory for key streams."
  }
];

const checklistSteps = [
  { num: 1, title: "Language Proficiency", desc: "Take IELTS (General) or CELPIP exam to prove language skills (aim for CLB 9+ to secure maximum points)." },
  { num: 2, title: "Credential Evaluation (ECA)", desc: "Verify your academic degrees/diplomas via WES or equivalent Canadian agencies to align with Canadian standards." },
  { num: 3, title: "Express Entry Profile", desc: "Create your official online profile and enter the Comprehensive Ranking System (CRS) pool to compete globally." },
  { num: 4, title: "PNP Nomination (Optional)", desc: "Submit Expressions of Interest (EOI) directly to Canadian provinces for a massive +600 points boost." },
  { num: 5, title: "Invitation to Apply (ITA)", desc: "Receive a formal invitation from the IRCC to submit your residency application when score cuts fall below your tally." },
  { num: 6, title: "Submit PR Application", desc: "Upload complete documents, reference letters, medical records, and police clearances within 60 days of getting ITA." },
  { num: 7, title: "Passport Stamp & COPR", desc: "Receive your Confirmation of Permanent Residence (COPR) and passport stamp to land in Canada as a permanent resident." }
];

export default function CanadaPR() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState(null);
  const [activePnp, setActivePnp] = useState(0);

  const [calculatorInputs, setCalculatorInputs] = useState({
    age: "110",
    education: "120",
    experience: "50",
    language: "136"
  });

  const totalScore =
    parseInt(calculatorInputs.age) +
    parseInt(calculatorInputs.education) +
    parseInt(calculatorInputs.experience) +
    parseInt(calculatorInputs.language);

  // SVG Radial Gauge parameters
  const maxScore = 500; // Reference score for base core factors
  const percentage = Math.min(100, (totalScore / maxScore) * 100);
  const strokeDasharray = 326.7;
  const strokeDashoffset = strokeDasharray - (strokeDasharray * percentage) / 100;

  const drawComparisons = [
    { category: "STEM Draw Cutoff", cutoff: 485, status: totalScore >= 485 },
    { category: "General Entry Cutoff", cutoff: 524, status: totalScore >= 524 },
    { category: "Healthcare Draw Cutoff", cutoff: 422, status: totalScore >= 422 },
    { category: "French Speakers Cutoff", cutoff: 388, status: totalScore >= 388 }
  ];

  const getRecommendation = (score) => {
    if (score >= 400) {
      return {
        label: "Excellent Eligibility",
        desc: "High chance of selection under Express Entry federal draws. You're an ideal candidate to file your profile immediately.",
        color: "#c51a1b"
      };
    } else if (score >= 350) {
      return {
        label: "Strong Match for PNPs",
        desc: "Highly competitive for Provincial Nominee Programs (PNPs). Boosting your IELTS score slightly can unlock direct Federal draws.",
        color: "#f2a65a"
      };
    } else if (score >= 300) {
      return {
        label: "Moderate Eligibility",
        desc: "Eligible for specific provincial draws or targeted category draws. Consider partner points optimization or study pathways.",
        color: "#36948c"
      };
    } else {
      return {
        label: "Action Needed to Optimize",
        desc: "We recommend boosting your credentials. Explore provincial sponsorships, study pathways, or securing a Canadian job offer.",
        color: "#64748b"
      };
    }
  };

  const recommendation = getRecommendation(totalScore);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "https://veabroad.com"}/api/consultationForm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          category: "Canada PR",
          source: "Canada PR page",
          inquiryType: "Canada PR",
          description: `Canada PR Enquiry. Message: ${formData.message}`
        })
      });
      const result = await response.json();
      if (result.status === "success") {
        setToast({ message: "Enquiry submitted successfully! Our team will contact you.", type: "success" });
        setFormData({ name: "", email: "", phone: "", message: "" });
        setSubmitted(true);
      } else {
        setToast({ message: "Submission failed. Please try again.", type: "error" });
      }
    } catch (error) {
      setToast({ message: "Network error. Please try again.", type: "error" });
    }
    setTimeout(() => setToast(null), 4000);
  };

  return (
    <div className="canadapr-page">
      <Helmet>
        <title>Canada PR Pathways | Express Entry & CRS Calculator</title>
        <meta name="description" content="Learn how to get Canada PR from India. Check eligibility, estimate CRS scores with our points calculator, and explore PNPs & Express Entry streams." />
        <link rel="canonical" href="https://veabroad.com/canada-pr" />
        <meta property="og:title" content="Canada PR Pathways | Express Entry & CRS Calculator" />
        <meta property="og:description" content="Learn how to get Canada PR from India. Check eligibility, estimate CRS scores with our points calculator, and explore PNPs & Express Entry streams." />
        <meta property="og:url" content="https://veabroad.com/canada-pr" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Canada PR Pathways | Express Entry & CRS Calculator" />
        <meta name="twitter:description" content="Learn how to get Canada PR from India. Check eligibility, estimate CRS scores with our points calculator, and explore PNPs & Express Entry streams." />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Canada Permanent Residency Consultancy",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Vidya Education Abroad & Liba Migration",
              "url": "https://veabroad.com/canada-pr"
            },
            "areaServed": "IN",
            "description": "Comprehensive consulting for Express Entry programs, CRS score optimization, and Provincial Nominee pathways."
          })}
        </script>
      </Helmet>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* HERO SECTION */}
      <section className="canadapr-hero position-relative overflow-hidden">
        <div className="canadapr-hero-bg" style={{ backgroundImage: "url('/canada_hero.webp')" }} />
        <div className="canadapr-hero-overlay" />
        <div className="canadapr-hero-blob canadapr-blob-1" />
        <div className="canadapr-hero-blob canadapr-blob-2" />
        <div className="canadapr-hero-grid" />

        <div className="container position-relative z-2">
          <div className="row align-items-center g-5 py-5">
            <div className="col-lg-7">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="canadapr-hero-text"
              >
                <div className="canadapr-badge mb-3">🍁 Canada Permanent Residency</div>
                <h1>Fast-Track Your Journey <br /><span className="text-highlight">to Canada PR.</span></h1>
                <p>Canada is inviting skilled professionals, tech leads, and healthcare workers from across the globe. Secure a stable future with high-paying salaries, free universal healthcare, world-class public education, and a direct path to Canadian citizenship.</p>
                <div className="canadapr-hero-points mt-4">
                  <span><FaCheckCircle className="point-icon" /> Express Entry Draws</span>
                  <span><FaCheckCircle className="point-icon" /> PNP Fast-Track Nominations</span>
                  <span><FaCheckCircle className="point-icon" /> Safe & Welcome Environment</span>
                </div>
              </motion.div>
            </div>

            <div className="col-lg-5">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="canadapr-form-wrap"
              >
                {submitted ? (
                  <div className="canadapr-form-success text-center bg-white p-5 rounded-4 shadow-lg border">
                    <div className="success-icon fs-1 mb-3">✅</div>
                    <h3 className="text-red fw-bold">Thank You!</h3>
                    <p className="text-muted">Your PR profile details have been received. Our licensed consultation partners will contact you within 24 hours.</p>
                  </div>
                ) : (
                  <form className="canadapr-hero-form p-4 p-md-5 rounded-4 shadow-lg d-flex flex-column gap-3" onSubmit={handleSubmit}>
                    <h3 className="h5 fw-bold text-center mb-3">Request Free Profile Assessment</h3>
                    <input
                      type="text"
                      placeholder="Full Name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="form-control"
                    />
                    <input
                      type="tel"
                      placeholder="Mobile Number"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="form-control"
                    />
                    <input
                      type="email"
                      placeholder="Email Address"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="form-control"
                    />
                    <textarea
                      placeholder="Tell us about your qualification and work experience (e.g. Master's in IT, 3+ years experience)"
                      rows="3"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="form-control"
                    />
                    <button type="submit" className="canadapr-form-submit text-white py-3 border-0 rounded-3 fw-bold">
                      Submit Profile Inquiry
                    </button>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CANADA / LIFESTYLE GALLERY */}
      <section className="section-pr py-5 bg-white">
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-5">
              <motion.div
                initial={{ opacity: 0, x: -35 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6 }}
                className="canadapr-image-frame shadow-lg position-relative rounded-4 overflow-hidden"
              >
                <img 
                  src="/canada_lifestyle.webp" 
                  alt="Quality of life in Canada" 
                  className="img-fluid w-100 h-100 object-fit-cover" 
                  style={{ minHeight: "350px", filter: "brightness(0.95)" }}
                  loading="lazy"
                />
                <div className="image-card-caption p-3 text-white position-absolute bottom-0 start-0 w-100">
                  <h5 className="m-0 fw-bold">Live the Canadian Dream</h5>
                  <p className="m-0 small opacity-75">Unmatched career growth, safety, and cultural diversity.</p>
                </div>
              </motion.div>
            </div>

            <div className="col-lg-7">
              <motion.div
                initial={{ opacity: 0, x: 35 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6 }}
              >
                <div className="text-red mb-2 fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.85rem' }}>
                  Benefits of Permanent Residence
                </div>
                <h2 className="section-title text-start mb-4">Why Migrate to Canada?</h2>
                <div className="row g-4">
                  {[
                    {
                      icon: <FaHeartbeat />,
                      title: "Free Healthcare System",
                      desc: "Canada's universal public health insurance covers essential medical services, clinical checkups, and emergency hospital services for you and your dependents."
                    },
                    {
                      icon: <FaGraduationCap />,
                      title: "Top-Tier Public Education",
                      desc: "Your children gain access to premium public primary and secondary education free of cost, with high-subsidized tuition fee structures at globally ranked universities."
                    },
                    {
                      icon: <FaAward />,
                      title: "High Standard of Living",
                      desc: "Consistently ranked among the top 5 countries globally for quality of life, featuring robust clean environments, high wages, and rich public infrastructures."
                    },
                    {
                      icon: <FaShieldAlt />,
                      title: "Safety and Civic Inclusiveness",
                      desc: "Canada is recognized globally for social tolerance, equality, and high safety standards. Experience a warm, crime-free and multicultural environment."
                    }
                  ].map((adv, i) => (
                    <div className="col-md-6" key={i}>
                      <div className="benefit-card d-flex gap-3 align-items-start p-3 rounded-3 border">
                        <div className="benefit-icon p-2 rounded-3 d-flex align-items-center justify-content-center">
                          {adv.icon}
                        </div>
                        <div>
                          <h4 className="h6 fw-bold text-dark mb-1">{adv.title}</h4>
                          <p className="m-0 text-muted small" style={{ lineHeight: '1.4' }}>{adv.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CRS SCORE CALCULATOR SECTION */}
      <section className="section-pr py-5 light-bg position-relative">
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <motion.div
                initial={{ opacity: 0, x: -35 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6 }}
              >
                <div className="text-red mb-3">
                  <span className="fw-bold tracking-wide text-uppercase" style={{ fontSize: '0.85rem' }}>Comprehensive Ranking System</span>
                </div>
                <h2 className="section-title text-start">Calculate Your <span className="va-shine-text">CRS Points Estimate</span></h2>
                <p className="section-text text-muted">
                  The CRS score is a points-based model used by Immigration, Refugees and Citizenship Canada (IRCC) to evaluate profiles in the Express Entry pool. Candidates with scores higher than the draw cutoffs receive an Invitation to Apply (ITA).
                </p>
                <p className="section-text text-muted">
                  Use our interactive tracker below to select your qualifications and age, and check how your credentials stack up against real, recent cutoffs.
                </p>
                <div className="canadapr-info-alert p-3 rounded-3 mt-4 d-flex gap-3 align-items-start border">
                  <FaInfoCircle className="text-red fs-4 flex-shrink-0 mt-1" />
                  <p className="m-0 text-muted" style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
                    <strong>Note:</strong> This calculates core score estimates based on age, education, and language levels. A formal Express Entry application profile requires registered IELTS/CELPIP results, ECA credential checks, and professional reference documents.
                  </p>
                </div>
              </motion.div>
            </div>

            <div className="col-lg-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="canadapr-calculator-card p-4 rounded-4 shadow-lg border bg-white"
              >
                <h3 className="h5 fw-bold mb-4 text-center text-teal">Interactive Points Dashboard</h3>

                <div className="calculator-form d-flex flex-column gap-3">
                  <div className="form-group">
                    <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: '0.82rem' }}>Age Group</label>
                    <select
                      className="form-select"
                      value={calculatorInputs.age}
                      onChange={(e) => setCalculatorInputs({ ...calculatorInputs, age: e.target.value })}
                    >
                      <option value="110">Under 30 years (+110 points)</option>
                      <option value="95">30 - 34 years (+95 points)</option>
                      <option value="75">35 - 39 years (+75 points)</option>
                      <option value="45">40 - 44 years (+45 points)</option>
                      <option value="0">45+ years (+0 points)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: '0.82rem' }}>Education Level</label>
                    <select
                      className="form-select"
                      value={calculatorInputs.education}
                      onChange={(e) => setCalculatorInputs({ ...calculatorInputs, education: e.target.value })}
                    >
                      <option value="150">Doctoral Degree (PhD) (+150 points)</option>
                      <option value="135">Master's Degree (+135 points)</option>
                      <option value="128">Two or more credentials (+128 points)</option>
                      <option value="120">Bachelor's Degree (+120 points)</option>
                      <option value="112">3-Year Professional Diploma (+112 points)</option>
                      <option value="30">High School (+30 points)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: '0.82rem' }}>Skilled Work Experience (Outside Canada)</label>
                    <select
                      className="form-select"
                      value={calculatorInputs.experience}
                      onChange={(e) => setCalculatorInputs({ ...calculatorInputs, experience: e.target.value })}
                    >
                      <option value="50">3 or more years (+50 points)</option>
                      <option value="38">2 years (+38 points)</option>
                      <option value="25">1 year (+25 points)</option>
                      <option value="0">Under 1 year / None (+0 points)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: '0.82rem' }}>Language Proficiency (IELTS / CLB)</label>
                    <select
                      className="form-select"
                      value={calculatorInputs.language}
                      onChange={(e) => setCalculatorInputs({ ...calculatorInputs, language: e.target.value })}
                    >
                      <option value="136">CLB 9+ (e.g. IELTS 8777) (+136 points)</option>
                      <option value="116">CLB 8 (e.g. IELTS 7.5 overall) (+116 points)</option>
                      <option value="68">CLB 7 (e.g. IELTS 6.0 bands) (+68 points)</option>
                      <option value="0">Under CLB 7 (+0 points)</option>
                    </select>
                  </div>
                </div>

                <div className="calculator-results mt-4 pt-3 border-top">
                  <div className="row align-items-center g-3">
                    <div className="col-md-5 text-center">
                      <div className="position-relative d-inline-block">
                        <svg width="120" height="120" className="radial-gauge">
                          <circle cx="60" cy="60" r="52" className="radial-bg" strokeWidth="8" fill="none" stroke="#f1f5f9" />
                          <circle
                            cx="60"
                            cy="60"
                            r="52"
                            className="radial-progress animate-progress"
                            strokeWidth="8"
                            fill="none"
                            stroke={recommendation.color}
                            strokeDasharray={strokeDasharray}
                            strokeDashoffset={strokeDashoffset}
                            transform="rotate(-90 60 60)"
                            strokeLinecap="round"
                            style={{ transition: "stroke-dashoffset 0.6s ease" }}
                          />
                        </svg>
                        <div className="position-absolute top-50 start-50 translate-middle text-center">
                          <span className="d-block fw-extrabold fs-4 text-dark">{totalScore}</span>
                          <span className="d-block text-muted" style={{ fontSize: '0.65rem' }}>/{maxScore}</span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-7">
                      <div className="recommendation-badge px-3 py-2 rounded-3" style={{ borderLeft: `4px solid ${recommendation.color}`, background: '#f8fafc' }}>
                        <span className="fw-bold d-block mb-1 small" style={{ color: recommendation.color }}>
                          {recommendation.label}
                        </span>
                        <p className="m-0 text-muted" style={{ fontSize: '0.78rem', lineHeight: '1.4' }}>
                          {recommendation.desc}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Express Entry Baseline Draw Comparison */}
                  <div className="draw-comparison-section mt-4">
                    <h4 className="h6 fw-bold text-dark mb-3 text-start">Score Comparison vs Recent Category Draws</h4>
                    <div className="d-flex flex-column gap-2">
                      {drawComparisons.map((draw, index) => (
                        <div key={index} className="comparison-item d-flex justify-content-between align-items-center p-2 rounded bg-light border-start border-3" style={{ borderLeftColor: draw.status ? '#10b981' : '#cbd5e1', fontSize: '0.8rem' }}>
                          <span className="fw-medium text-muted">{draw.category} ({draw.cutoff} pts)</span>
                          {draw.status ? (
                            <span className="badge bg-success-light text-success fw-bold px-2 py-1 rounded">Qualified ✔</span>
                          ) : (
                            <span className="badge bg-light text-secondary border px-2 py-1 rounded">Below Cutoff</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPRESS ENTRY PROGRAMS */}
      <section className="section-pr py-5 bg-white">
        <div className="container py-4">
          <Reveal className="text-center mb-5">
            <div className="text-red fw-bold text-uppercase tracking-wider mb-2" style={{ fontSize: '0.85rem' }}>Federal Economic Pathways</div>
            <h2 className="section-title">Express Entry Application Streams</h2>
            <p className="text-muted mx-auto" style={{ maxWidth: '600px', fontSize: '0.95rem' }}>Express Entry operates as a pooled intake system managing highly qualified profiles under three major federal programs.</p>
          </Reveal>

          <div className="row g-4">
            {[
              {
                title: "Federal Skilled Worker Program (FSWP)",
                desc: "Optimized for foreign skilled professionals possessing international degrees and structured corporate experience in administrative, engineering, tech, or business sectors.",
                highlights: ["Requires equivalent Canadian bachelor's or ECA check", "Needs minimum 1 year continuous full-time skilled experience", "Required language target IELTS CLB 7+ overall"]
              },
              {
                title: "Canadian Experience Class (CEC)",
                desc: "Tailored for international student graduates and skilled professionals with official temporary working experience completed physically in Canadian regions.",
                highlights: ["Minimum 1 year local Canadian skilled job record required", "No settlement proof of funds necessary for CEC candidates", "Exempted from many restrictive documentation thresholds"]
              },
              {
                title: "Federal Skilled Trades Program (FSTP)",
                desc: "Designed specifically for qualified industrial tradespeople, commercial cooks, electrical technicians, and mining specialists with job proposals.",
                highlights: ["Valid Canadian corporate job offer of at least 1 year needed", "Minimum 2 years experience in designated trade sector required", "Lower initial language requirements (IELTS CLB 5 bands overall)"]
              }
            ].map((stream, idx) => (
              <div className="col-lg-4" key={idx}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="canadapr-stream-card p-4 rounded-4 shadow-sm border h-100 bg-white"
                >
                  <div className="stream-badge mb-3">Pathway {idx + 1}</div>
                  <h3 className="h5 fw-bold text-teal mb-3">{stream.title}</h3>
                  <p className="text-muted small mb-4" style={{ lineHeight: '1.6' }}>{stream.desc}</p>
                  <ul className="stream-points list-unstyled m-0 d-flex flex-column gap-2">
                    {stream.highlights.map((h, hIdx) => (
                      <li key={hIdx} className="small text-dark d-flex gap-2 align-items-start">
                        <FaCheckCircle className="text-teal flex-shrink-0 mt-1" />
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>{h}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROVINCIAL NOMINEE PROGRAMS (PNPs) - INTERACTIVE PANEL */}
      <section className="section-pr py-5 light-bg">
        <div className="container py-4">
          <Reveal className="text-center mb-5">
            <div className="text-red fw-bold text-uppercase tracking-wider mb-2" style={{ fontSize: '0.85rem' }}>Provincial Nominee Programs (PNPs)</div>
            <h2 className="section-title">Select Your Targeted Canadian Province</h2>
            <p className="text-muted mx-auto" style={{ maxWidth: '600px', fontSize: '0.95rem' }}>Different Canadian provinces manage their own immigration nomination allocations. Getting nominated awards you an instant 600 points.</p>
          </Reveal>

          <div className="row g-4 align-items-center">
            {/* Province Buttons Panel */}
            <div className="col-lg-5">
              <RevealGroup as="div" className="d-flex flex-column gap-3" stagger={0.08}>
                {pnpPrograms.map((p, idx) => (
                  <RevealItem
                    as="button"
                    direction="left"
                    key={idx}
                    onClick={() => setActivePnp(idx)}
                    className={`province-selector-btn text-start p-3 rounded-4 border d-flex align-items-center justify-content-between transition-all ${activePnp === idx ? 'active-pnp shadow' : 'bg-white'}`}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className="province-icon-wrap p-2 rounded-3 bg-light">
                        <FaMapMarkerAlt className="text-red" />
                      </div>
                      <div>
                        <h4 className="h6 fw-bold m-0 text-dark">{p.name}</h4>
                        <span className="text-muted small" style={{ fontSize: '0.72rem' }}>{p.stream}</span>
                      </div>
                    </div>
                    <FaChevronRight className={`arrow-icon ${activePnp === idx ? 'rotated' : ''}`} />
                  </RevealItem>
                ))}
              </RevealGroup>
            </div>

            {/* Province Details Card Panel */}
            <div className="col-lg-7">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activePnp}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="province-details-card p-4 p-md-5 rounded-4 shadow border bg-white"
                >
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 border-bottom pb-3 mb-4">
                    <div>
                      <span className="badge bg-danger-light text-red fw-bold mb-1">🍁 Active PNP Draw Stream</span>
                      <h3 className="h4 fw-bold text-teal m-0">{pnpPrograms[activePnp].name}</h3>
                    </div>
                    <div className="text-md-end">
                      <span className="text-muted d-block small" style={{ fontSize: '0.75rem' }}>Average Starting Salary</span>
                      <strong className="text-teal fs-5">{pnpPrograms[activePnp].wages}</strong>
                    </div>
                  </div>

                  <p className="text-muted mb-4 small" style={{ lineHeight: '1.6' }}>{pnpPrograms[activePnp].details}</p>

                  <div className="mb-4">
                    <h5 className="h6 fw-bold text-dark mb-3">🔥 Highly In-Demand Local Occupations</h5>
                    <div className="row g-2">
                      {pnpPrograms[activePnp].demands.map((demand, dIdx) => (
                        <div className="col-6 col-md-4" key={dIdx}>
                          <div className="demand-tag p-2 rounded text-center small text-teal border">
                            {demand}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-2 text-muted small">
                      <FaAward className="text-red" />
                      <span>Boosts your CRS Score by +600 points</span>
                    </div>
                    <a href="#consult-form" className="btn-province-query text-decoration-none small fw-bold text-white px-4 py-2 rounded-3" style={{ background: '#1d4e50' }}>
                      Enquire for {pnpPrograms[activePnp].name.split(" ")[0]}
                    </a>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Collateral Provinces banner */}
          <Reveal direction="up" className="row mt-5">
            <div className="col-12">
              <div className="provinces-banner-wrap rounded-4 overflow-hidden position-relative shadow border va-hover-lift" style={{ height: '220px' }}>
                <img 
                  src="/canada_scenic_highway.webp" 
                  alt="Scenic highway winding through the Canadian Rocky Mountains" 
                  className="w-100 h-100 object-fit-cover" 
                  style={{ filter: "brightness(0.55)" }}
                  loading="lazy"
                />
                <div className="position-absolute inset-0 d-flex flex-column justify-content-center align-items-center text-center p-4 text-white">
                  <h4 className="fw-bold mb-2">Ready to explore Provincial Pathways?</h4>
                  <p className="m-0 small opacity-75 max-width-600 mb-3">Multiple target provinces offer specialized sub-streams matching candidate domains dynamically.</p>
                  <a href="#consult-form" className="btn btn-light btn-sm fw-bold px-4 rounded-3 text-teal">Get Eligibility Assessment</a>
                </div>
              </div>
            </div>
          </Reveal>

        </div>
      </section>

      {/* CANADA PR STEP-BY-STEP CHECKLIST */}
      <section className="section-pr py-5 bg-white">
        <div className="container py-4">
          <Reveal className="text-center mb-5">
            <div className="text-red fw-bold text-uppercase tracking-wider mb-2" style={{ fontSize: '0.85rem' }}>Checklist and Stepper</div>
            <h2 className="section-title">Your Step-By-Step PR Journey</h2>
            <p className="text-muted mx-auto" style={{ maxWidth: '600px', fontSize: '0.95rem' }}>We break down the formal Permanent Residency filing cycle into 7 structured steps from start to landing.</p>
          </Reveal>

          <div className="vertical-timeline-container position-relative">
            {/* Central vertical timeline connector */}
            <div className="vertical-timeline-line" />

            <div className="vertical-timeline-items">
              {checklistSteps.map((step, idx) => {
                const isLeft = idx % 2 === 0;
                return (
                  <div key={idx} className="vertical-timeline-item d-flex align-items-center justify-content-start justify-content-lg-center position-relative mb-5">
                    
                    {/* Circle Node on Timeline Line */}
                    <div className="vertical-timeline-node d-flex align-items-center justify-content-center rounded-circle text-white fw-bold">
                      {step.num}
                    </div>

                    <div className={`col-lg-5 col-12 vertical-timeline-card-wrap ${isLeft ? 'offset-lg-0 me-lg-auto text-lg-end pe-lg-5 ps-lg-0' : 'offset-lg-6 ms-lg-auto text-lg-start ps-lg-5 pe-lg-0'}`}>
                      <motion.div
                        initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.5, delay: 0.05 }}
                        className="canadapr-checklist-item p-4 rounded-4 shadow-sm border bg-white position-relative"
                      >
                        <span className="badge bg-danger-light text-red fw-bold mb-2">Step 0{step.num}</span>
                        <h4 className="fw-bold text-teal mb-2" style={{ fontSize: '1.1rem' }}>{step.title}</h4>
                        <p className="text-muted m-0 small" style={{ lineHeight: '1.6' }}>{step.desc}</p>
                      </motion.div>
                    </div>

                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section id="consult-form" className="canadapr-cta py-5 overflow-hidden text-center position-relative">
        <div className="canadapr-hero-blob canadapr-blob-1" style={{ top: '-10%', left: '30%', width: '300px', height: '300px' }} />
        <div className="canadapr-hero-grid" />
        <div className="container position-relative z-2 py-4">
          <Reveal direction="zoom">
            <h2 className="text-white mb-3">Begin Your Canada PR Process Today</h2>
            <p className="lead text-white-50 mb-5 mx-auto" style={{ maxWidth: '600px' }}>Schedule a formal profiling session with our registered consultants at Liba Migration Services and Vidya Education Abroad to maximize your CRS score points.</p>
            <div className="cta-buttons d-flex justify-content-center gap-3 flex-wrap">
              <MagneticButton as="span">
                <Link to="/consultationForm" className="canadapr-btn-primary py-3 px-4 rounded-3 text-decoration-none fw-bold shadow-md">
                  Book Comprehensive Consultation
                </Link>
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
